import { useState, type FormEvent } from 'react';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardContent, CardHeader, CardTitle, Alert } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { updateMe, requestEmailChange } from '@/services/authApi';
import { getErrorCode } from '@/utils/errors';
import type { UserUpdate } from '@/types/auth';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function getApiStatusCode(err: unknown): number | null {
  if (err && typeof err === 'object' && 'response' in err) {
    const axiosErr = err as { response?: { status?: number } };
    return axiosErr.response?.status ?? null;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Input style (shared)
// ---------------------------------------------------------------------------

const inputStyle: React.CSSProperties = {
  width: '100%',
  height: '44px',
  borderRadius: '10px',
  border: '1px solid var(--color-border-strong)',
  backgroundColor: 'var(--color-surface-raised-3)',
  color: 'var(--color-text-primary)',
  fontSize: '14px',
  padding: '0 14px',
  boxSizing: 'border-box',
  transition: 'border-color 180ms ease, box-shadow 180ms ease',
};

// ---------------------------------------------------------------------------
// Handedness options
// ---------------------------------------------------------------------------

type HandednessValue = 'left' | 'right' | 'ambidextrous' | '';

const HANDEDNESS_OPTIONS: Array<{ value: HandednessValue; label: string }> = [
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
  { value: 'ambidextrous', label: 'Ambidextrous' },
  { value: '', label: 'Not specified' },
];

// ---------------------------------------------------------------------------
// Section: Identity (display_name + email)
// ---------------------------------------------------------------------------

function IdentitySection() {
  const { user, refreshUser } = useAuth();

  const [displayName, setDisplayName] = useState(user?.display_name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');

  const [nameSaving, setNameSaving] = useState(false);
  const [nameStatus, setNameStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [nameError, setNameError] = useState<string | null>(null);

  const [emailSaving, setEmailSaving] = useState(false);
  // "pending" = verification email sent; the email is NOT yet changed
  const [emailStatus, setEmailStatus] = useState<'idle' | 'pending' | 'error'>('idle');
  const [emailError, setEmailError] = useState<string | null>(null);
  // Track the address we sent the link to, so we can show it in the confirmation message
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);

  const handleSaveName = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = displayName.trim();
    if (trimmed.length === 0) {
      setNameError('Display name cannot be empty.');
      return;
    }
    if (trimmed.length > 100) {
      setNameError('Display name must be 100 characters or fewer.');
      return;
    }
    if (nameSaving) return;
    setNameSaving(true);
    setNameStatus('idle');
    setNameError(null);
    try {
      const update: UserUpdate = { display_name: trimmed };
      await updateMe(update);
      await refreshUser();
      setNameStatus('success');
    } catch (err) {
      setNameStatus('error');
      setNameError(err instanceof Error ? err.message : 'Failed to save name. Please try again.');
    } finally {
      setNameSaving(false);
    }
  };

  const handleSaveEmail = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    if (emailSaving) return;
    setEmailSaving(true);
    setEmailStatus('idle');
    setEmailError(null);
    setPendingEmail(null);
    try {
      await requestEmailChange(trimmed);
      setPendingEmail(trimmed);
      setEmailStatus('pending');
    } catch (err) {
      setEmailStatus('error');
      const statusCode = getApiStatusCode(err);
      const code = getErrorCode(err);
      if (code === 'same_email') {
        setEmailError('That is already your email address.');
      } else if (statusCode === 409 || code === 'email_taken') {
        setEmailError('That email address is already in use.');
      } else if (statusCode === 429 || code === 'rate_limited') {
        setEmailError('Too many requests. Please wait a moment and try again.');
      } else if (statusCode === 422) {
        setEmailError('Please enter a valid email address.');
      } else {
        setEmailError('Failed to request email change. Please try again.');
      }
    } finally {
      setEmailSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Identity</CardTitle>
      </CardHeader>
      <CardContent>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

          {/* Display name */}
          <form onSubmit={handleSaveName} noValidate style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <label
                htmlFor="profile-display-name"
                style={{ display: 'block', fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '6px' }}
              >
                Display name
              </label>
              <input
                id="profile-display-name"
                type="text"
                autoComplete="name"
                placeholder="Your name"
                value={displayName}
                onChange={(e) => { setDisplayName(e.target.value); setNameStatus('idle'); setNameError(null); }}
                disabled={nameSaving}
                maxLength={100}
                style={inputStyle}
              />
              {nameError && (
                <p style={{ fontSize: '12px', color: 'var(--color-signal-bad)', marginTop: '4px' }}>
                  {nameError}
                </p>
              )}
            </div>
            {nameStatus === 'success' && <Alert variant="success">Display name updated.</Alert>}
            {nameStatus === 'error' && !nameError && <Alert variant="error">Failed to save. Try again.</Alert>}
            <div>
              <Button type="submit" variant="primary" disabled={nameSaving}>
                {nameSaving ? 'Saving...' : 'Update name'}
              </Button>
            </div>
          </form>

          <hr style={{ border: 'none', borderTop: '1px solid var(--color-border-subtle)' }} />

          {/* Email */}
          <form onSubmit={handleSaveEmail} noValidate style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <label
                htmlFor="profile-email"
                style={{ display: 'block', fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '6px' }}
              >
                Email address
              </label>
              <input
                id="profile-email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailStatus('idle');
                  setEmailError(null);
                  setPendingEmail(null);
                }}
                disabled={emailSaving}
                style={inputStyle}
              />
              {emailError && (
                <p style={{ fontSize: '12px', color: 'var(--color-signal-bad)', marginTop: '4px' }}>
                  {emailError}
                </p>
              )}
            </div>
            {emailStatus === 'pending' && pendingEmail && (
              <Alert variant="success">
                We&apos;ve sent a confirmation link to <strong>{pendingEmail}</strong>. Click it to finish
                changing your email. Your current email stays active until you confirm.
              </Alert>
            )}
            {emailStatus === 'error' && !emailError && (
              <Alert variant="error">Failed to request email change. Try again.</Alert>
            )}
            <div>
              <Button type="submit" variant="primary" disabled={emailSaving || emailStatus === 'pending'}>
                {emailSaving ? 'Sending...' : emailStatus === 'pending' ? 'Confirmation sent' : 'Update email'}
              </Button>
            </div>
          </form>

        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Section: Account info (read-only role + tier)
// ---------------------------------------------------------------------------

function AccountInfoSection() {
  const { user } = useAuth();
  if (!user) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Account</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-text-secondary">Role</span>
          <span className="text-text-primary font-medium capitalize">{user.role}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-text-secondary">Plan</span>
          <span className="text-text-primary font-medium capitalize">{user.tier}</span>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Section: Movement Preferences (handedness)
// ---------------------------------------------------------------------------

function HandednessSection() {
  const { user, refreshUser } = useAuth();

  const [handedness, setHandedness] = useState<HandednessValue>(
    (user?.handedness as HandednessValue) ?? ''
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    setSaveStatus('idle');
    setErrorMessage(null);

    const update: UserUpdate = {
      handedness: handedness === '' ? null : handedness,
    };

    try {
      await updateMe(update);
      await refreshUser();
      setSaveStatus('success');
    } catch (err) {
      setSaveStatus('error');
      setErrorMessage(
        err instanceof Error ? err.message : 'Failed to save preferences. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Movement Preferences</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-text-secondary">
            Dominant Side
          </label>
          <div className="flex flex-wrap gap-2">
            {HANDEDNESS_OPTIONS.map(({ value, label }) => (
              <button
                key={label}
                type="button"
                onClick={() => {
                  setHandedness(value);
                  setSaveStatus('idle');
                }}
                className={`px-4 py-2 rounded-sm-control text-sm font-medium border transition-colors ${
                  handedness === value
                    ? 'bg-accent text-text-inverse border-accent'
                    : 'bg-dark-elevated-2 text-text-primary border-border-subtle hover:bg-dark-elevated-1'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="text-xs text-text-muted leading-relaxed">
            Your dominant side. For most sports this matches your handedness — fencers, throwers,
            and batters use their dominant hand; soccer players use their dominant kicking foot.
            Cross-dominant athletes (e.g., right-handed but left-footed kickers) — use the side
            that performs the active role in the movements you train. Set "Ambidextrous" if you
            switch stances regularly.
          </p>
        </div>

        {saveStatus === 'success' && (
          <Alert variant="success">Preferences saved.</Alert>
        )}
        {saveStatus === 'error' && errorMessage && (
          <Alert variant="error">{errorMessage}</Alert>
        )}

        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isSaving}
          className="w-full sm:w-auto"
        >
          {isSaving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Profile page
// ---------------------------------------------------------------------------

function Profile() {
  const { user } = useAuth();

  if (!user) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-text-muted">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <PageHeader
        title="Profile"
        description="Manage your identity and movement preferences"
      />
      <IdentitySection />
      <HandednessSection />
      <AccountInfoSection />
    </div>
  );
}

export default Profile;
